import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { useAuth } from './AuthContext';

const UserSettingsContext = createContext(null);

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings deve essere usato dentro UserSettingsProvider');
  }
  return context;
};

export const UserSettingsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Carica impostazioni quando l'utente è autenticato
  useEffect(() => {
    const loadSettings = async () => {
      if (isAuthenticated && !hasLoaded) {
        setIsLoading(true);
        try {
          const userSettings = await settingsAPI.getSettings();
          setSettings(userSettings);
          setHasLoaded(true);
        } catch (error) {
          console.error('Errore caricamento impostazioni:', error);
        } finally {
          setIsLoading(false);
        }
      } else if (!isAuthenticated) {
        // Reset quando l'utente si disconnette
        setSettings(null);
        setHasLoaded(false);
      }
    };

    loadSettings();
  }, [isAuthenticated, hasLoaded]);

  const saveSettings = async (newSettings) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Non autenticato' };
    }

    try {
      await settingsAPI.saveSettings(newSettings);
      setSettings(newSettings);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resetToDefault = async () => {
    if (!isAuthenticated) {
      return { success: false, error: 'Non autenticato' };
    }

    try {
      const data = await settingsAPI.resetSettings();
      setSettings(data.settings);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateSettings = (updatedSettings) => {
    setSettings(prev => ({
      ...prev,
      ...updatedSettings
    }));
  };

  const value = {
    settings,
    isLoading,
    hasLoaded,
    saveSettings,
    resetToDefault,
    updateSettings
  };

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
};

